import { useState, useRef, useEffect } from "react"
import { useSelector } from "react-redux"
import { useTareas } from "../context/TareasContext"
import { FaFolder, FaSpinner, FaPlus, FaTasks, FaEllipsisV, FaPen, FaArchive, FaTrash, FaCheck, FaTimes, FaBoxOpen, FaChevronDown, FaChevronRight, FaLock, FaGlobe, FaUsers } from "react-icons/fa"
import Button from "@mui/material/Button"
import InputFormValue from "../../ui/components/InputFormValue"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"

const PRESET_COLORS = ["#FE6400","#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#06B6D4"]

const viewersToIds = (viewers) =>
  (viewers || []).map((v) => (typeof v === "object" && v?.id ? v.id : v)).filter(Boolean)

const ProjectCard = ({ project, onSelect, archived = false, onUnarchive }) => {
  const user = useSelector((s) => s.auth.user)
  const { updateProject, archiveProject, deleteProject, workers } = useTareas()
  const creatorId = project.createdBy?.id ?? project.createdBy
  const isProjectCreator = Boolean(user?.id && creatorId && String(user.id) === String(creatorId))
  const [menuOpen, setMenuOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(project.name)
  const [editDesc, setEditDesc] = useState(project.description || "")
  const [editColor, setEditColor] = useState(project.color || "#6B7280")
  const [editVisibility, setEditVisibility] = useState(project.visibility || "publico")
  const [editViewers, setEditViewers] = useState(() => viewersToIds(project.viewers))
  const [editTags, setEditTags] = useState(() =>
    (project.tags || []).map((t) => ({
      id: t.id,
      name: t.name || "",
      color: t.color || "#6B7280",
      archived: Boolean(t.archived),
    }))
  )
  const menuRef = useRef(null)
  const inputRef = useRef(null)
  const editTagsInitialJsonRef = useRef("")

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const startEdit = (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    setEditName(project.name)
    setEditDesc(project.description || "")
    setEditColor(project.color || "#6B7280")
    setEditVisibility(project.visibility || "publico")
    setEditViewers(viewersToIds(project.viewers))
    const tagSnap = (project.tags || []).map((t) => ({
      id: t.id,
      name: t.name || "",
      color: t.color || "#6B7280",
      archived: Boolean(t.archived),
    }))
    setEditTags(tagSnap)
    editTagsInitialJsonRef.current = JSON.stringify(
      tagSnap.map((t) => ({ id: t.id, name: t.name.trim(), color: t.color, archived: t.archived }))
    )
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const saveEdit = async (e) => {
    e?.stopPropagation()
    if (!editName.trim()) return
    if (
      isProjectCreator &&
      editVisibility === "privado" &&
      editViewers.length === 0
    ) {
      window.alert("En privado debes elegir al menos un trabajador con acceso.")
      return
    }
    const payload = {
      name: editName.trim(),
      description: editDesc.trim(),
      color: editColor,
    }
    const normalizedTags = editTags.map((t) => ({
      id: t.id,
      name: (t.name || "").trim() || "Sin nombre",
      color: t.color || "#6B7280",
      archived: Boolean(t.archived),
    }))
    const tagsJson = JSON.stringify(
      normalizedTags.map((t) => ({ id: t.id, name: t.name.trim(), color: t.color, archived: t.archived }))
    )
    if (tagsJson !== editTagsInitialJsonRef.current) {
      payload.tags = normalizedTags
    }
    if (isProjectCreator) {
      payload.visibility = editVisibility || "publico"
      payload.viewers = editVisibility === "privado" ? editViewers : []
    }
    await updateProject(project.id, payload)
    setEditing(false)
  }

  const handleArchive = async (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    await archiveProject(project.id)
  }

  const handleUnarchive = async (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    if (onUnarchive) onUnarchive(project.id)
  }

  const handleDelete = async (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    await deleteProject(project.id)
  }

  if (editing) {
    return (
      <div
        className="flex flex-col items-stretch text-left p-4 rounded-xl border-2 border-orange-300 bg-white shadow-md"
        onClick={e => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={editName}
          onChange={e => setEditName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(false) }}
          className="text-sm font-semibold text-gray-800 border border-gray-200 rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
          placeholder="Nombre del proyecto"
        />
        <textarea
          value={editDesc}
          onChange={e => setEditDesc(e.target.value)}
          rows={2}
          className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
          placeholder="Descripción (opcional)"
        />
        <div className="flex items-center gap-1.5 mb-3">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              onClick={() => setEditColor(c)}
              className="w-5 h-5 rounded-full border-2 transition-all flex-shrink-0"
              style={{
                backgroundColor: c,
                borderColor: editColor === c ? "#1F2937" : "transparent",
                transform: editColor === c ? "scale(1.2)" : "scale(1)",
              }}
            />
          ))}
        </div>

        <div className="mb-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tags del proyecto</p>
          <div className="flex flex-col gap-2 max-h-52 overflow-y-auto pr-0.5">
            {editTags.map((t, idx) => (
              <div
                key={t.id}
                className={`flex flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1.5 ${
                  t.archived ? "border-dashed border-gray-200 bg-gray-50" : "border-gray-200 bg-white"
                }`}
              >
                <input
                  value={t.name}
                  onChange={(e) => {
                    const v = e.target.value
                    setEditTags((prev) => prev.map((x, i) => (i === idx ? { ...x, name: v } : x)))
                  }}
                  className="text-xs flex-1 min-w-[100px] border border-gray-100 rounded px-1.5 py-0.5"
                  disabled={t.archived}
                />
                <input
                  type="color"
                  value={t.color?.startsWith("#") ? t.color : "#6B7280"}
                  onChange={(e) => {
                    const v = e.target.value
                    setEditTags((prev) => prev.map((x, i) => (i === idx ? { ...x, color: v } : x)))
                  }}
                  className="w-6 h-6 rounded cursor-pointer border-0 p-0 flex-shrink-0"
                  disabled={t.archived}
                />
                <button
                  type="button"
                  className="text-[10px] text-gray-500 hover:text-orange-600 px-1"
                  onClick={() =>
                    setEditTags((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, archived: !x.archived } : x))
                    )
                  }
                >
                  {t.archived ? "Restaurar" : "Archivar"}
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="mt-1.5 text-[10px] text-orange-600 font-medium hover:underline"
            onClick={() => {
              const uuid =
                typeof window !== "undefined" && window.crypto?.randomUUID
                  ? window.crypto.randomUUID()
                  : `tag_${Date.now()}_${Math.random().toString(16).slice(2)}`
              setEditTags((prev) => [...prev, { id: uuid, name: "Nuevo tag", color: "#6B7280", archived: false }])
            }}
          >
            + Añadir tag
          </button>
        </div>

        {isProjectCreator && (
          <>
            <div className="mb-2">
              <InputFormValue
                title="Visibilidad"
                type="select"
                value={editVisibility}
                setValue={(v) => {
                  setEditVisibility(v)
                  if (v === "publico") setEditViewers([])
                }}
                icon={<FaLock />}
                options={[
                  { value: "publico", label: "Público" },
                  { value: "privado", label: "Privado" },
                ]}
              />
            </div>

            {editVisibility === "privado" && (
              <div className="mb-2">
                <InputFormValue
                  title="Quién puede ver"
                  type="multiSelect"
                  value={editViewers}
                  setValue={setEditViewers}
                  icon={<FaUsers />}
                  placeholder="Marca a los trabajadores con acceso al proyecto"
                  options={workers.map((w) => ({
                    value: w.id,
                    label: formatWorkerDisplayName(w.name || w.email) || w.id,
                  }))}
                  maxHeight="max-h-40"
                />
              </div>
            )}
          </>
        )}

        <div className="flex gap-2">
          <button
            onClick={saveEdit}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <FaCheck className="text-[10px]" /> Guardar
          </button>
          <button
            onClick={() => setEditing(false)}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="text-[10px]" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`group relative ${archived ? "opacity-60" : ""}`}>
      <button
        onClick={() => !archived && onSelect(project)}
        className={`flex flex-col items-stretch text-left p-4 rounded-xl border bg-white transition-all duration-200 w-full ${
          archived
            ? "border-dashed border-gray-300 cursor-default"
            : "border-gray-200 hover:border-orange-300 hover:shadow-md"
        }`}
      >
        <div className="flex items-start justify-between">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
            style={{ backgroundColor: project.color ? `${project.color}20` : "#F3F4F6" }}
          >
            <FaFolder className="text-lg" style={{ color: project.color || "#6B7280" }} />
          </div>
          <div className="flex flex-col items-end gap-1">
            {project.visibility === "privado" ? (
              <span className="text-[9px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                <FaLock className="text-[8px]" /> Privado
              </span>
            ) : (
              <span className="text-[9px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                <FaGlobe className="text-[8px]" /> Público
              </span>
            )}
            {archived && (
              <span className="text-[9px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                Archivado
              </span>
            )}
          </div>
        </div>
        <h3 className={`font-semibold truncate transition-colors ${archived ? "text-gray-500" : "text-gray-800 group-hover:text-orange-600"}`}>
          {project.name}
        </h3>
        {project.description && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{project.description}</p>
        )}
        {!archived && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <FaTasks className="text-[10px]" />
            <span>Ver tareas</span>
          </div>
        )}
      </button>

      {/* Menu */}
      <div ref={menuRef} className="absolute top-2 right-2">
        <button
          onClick={e => { e.stopPropagation(); setMenuOpen(v => !v) }}
          className={`p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all ${archived ? "" : "opacity-0 group-hover:opacity-100"}`}
        >
          <FaEllipsisV className="text-xs" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 min-w-[140px]">
            {archived ? (
              <>
                <button
                  onClick={handleUnarchive}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaBoxOpen className="text-[10px] text-gray-400" /> Desarchivar
                </button>
                <hr className="my-1 border-gray-100" />
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 transition-colors"
                >
                  <FaTrash className="text-[10px]" /> Eliminar
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={startEdit}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaPen className="text-[10px] text-gray-400" /> Editar
                </button>
                <button
                  onClick={handleArchive}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaArchive className="text-[10px] text-gray-400" /> Archivar
                </button>
                <hr className="my-1 border-gray-100" />
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 transition-colors"
                >
                  <FaTrash className="text-[10px]" /> Eliminar
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const ProjectsView = () => {
  const { projects, projectsLoading, setSelectedProject, setCreateProjectModalOpen, fetchArchivedProjects, unarchiveProject } = useTareas()
  const [showArchived, setShowArchived] = useState(false)
  const [archivedProjects, setArchivedProjects] = useState([])
  const [archivedLoading, setArchivedLoading] = useState(false)

  const toggleArchived = async () => {
    if (!showArchived && archivedProjects.length === 0) {
      setArchivedLoading(true)
      const docs = await fetchArchivedProjects()
      setArchivedProjects(docs)
      setArchivedLoading(false)
    }
    setShowArchived(v => !v)
  }

  const handleUnarchive = async (projectId) => {
    const doc = await unarchiveProject(projectId)
    if (doc) setArchivedProjects(prev => prev.filter(p => p.id !== projectId))
  }

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <FaSpinner className="animate-spin text-2xl text-orange-400" />
          <span className="text-sm">Cargando proyectos...</span>
        </div>
      </div>
    )
  }

  if (projects.length === 0 && !showArchived) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4 text-gray-400 text-center px-4">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
            <FaFolder className="text-4xl text-gray-300" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">No hay proyectos</p>
            <p className="text-xs text-gray-400 mt-1">Crea un proyecto para comenzar a gestionar tareas</p>
          </div>
          <Button
            onClick={() => setCreateProjectModalOpen(true)}
            variant="outlined"
            size="small"
            startIcon={<FaPlus />}
            sx={{
              fontFamily: "poppins",
              textTransform: "none",
              borderRadius: "8px",
              borderColor: "#ff6400",
              color: "#ff6400",
              mt: 2,
              "&:hover": { borderColor: "#bb4a03", backgroundColor: "#fff7f0" },
            }}
          >
            Crear primer proyecto
          </Button>
          <button
            onClick={toggleArchived}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mt-2 transition-colors"
          >
            <FaArchive className="text-[10px]" /> Ver proyectos archivados
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {projects.map(project => (
          <ProjectCard
            key={project.id}
            project={project}
            onSelect={setSelectedProject}
          />
        ))}
      </div>

      {/* Sección archivados */}
      <div className="mt-8">
        <button
          onClick={toggleArchived}
          className="flex items-center gap-2 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
        >
          {showArchived ? <FaChevronDown className="text-[9px]" /> : <FaChevronRight className="text-[9px]" />}
          <FaArchive className="text-[10px]" />
          Archivados
          {showArchived && archivedProjects.length > 0 && (
            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{archivedProjects.length}</span>
          )}
        </button>

        {showArchived && (
          <div className="mt-4">
            {archivedLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-4">
                <FaSpinner className="animate-spin text-sm text-orange-400" />
                Cargando archivados...
              </div>
            ) : archivedProjects.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No hay proyectos archivados</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {archivedProjects.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={() => {}}
                    archived
                    onUnarchive={handleUnarchive}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectsView
